/** /notification_themes/ — cross-tenant Notification_Theme catalog
 *  (list + CRUD + used_by).
 *
 * A notification theme styles deliveries: a DTL email shell wrapped around the
 * rendered email body ({{ body }} insertion point), free-form design tokens
 * exposed to templates as `theme.*` (deep-merged over the operator color
 * theme), optional CSS inlining (premailer) at send time, and per-channel
 * text signatures appended to sms/chat/whatsapp/airbnb bodies.
 * `tour_operator` is nullable: a null tenant is a system-wide theme usable by
 * every operator.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). Notification_Theme has no optimistic-concurrency
 * token, so writes are last-writer-wins (no conflict banner, like blocks).
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export interface NotificationThemeListItem {
  id: number;
  /** Owning tenant. Null for system-wide themes (`Notification_Theme
   *  .tour_operator` is nullable, serialised as `tenant: null`). */
  tenant: TenantRef | null;
  /** Referenced by the operator config `notifications.default_theme` value
   *  (identifier grammar). */
  codename: string;
  name: string;
  description: string;
  enabled: boolean;
  /** DTL document wrapping the rendered email body at delivery; must contain
   *  the `{{ body }}` insertion point when non-empty. Empty = no wrapping. */
  email_shell: string;
  /** Free-form design tokens exposed to templates as `theme.*`, deep-merged
   *  over the Tour_Operator_Color theme dict. */
  tokens: Record<string, unknown>;
  /** Run premailer over the wrapped email body at send time. */
  inline_css: boolean;
  /** DTL snippets appended to the respective text-channel bodies at delivery. */
  sms_signature: string;
  chat_signature: string;
  whatsapp_signature: string;
  airbnb_signature: string;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface NotificationThemeDetail extends NotificationThemeListItem {
  /** Reference count of the PROTECTed `NDM.theme` FK that blocks a delete (409). */
  used_by: {
    delivery_methods: number;
  };
}

/** Create/update body. `codename` required on create; `tour_operator` null =
 *  system-wide. Shell/token validation runs server-side. */
export interface NotificationThemeWrite {
  tour_operator?: number | null;
  codename?: string;
  name?: string;
  description?: string;
  enabled?: boolean;
  email_shell?: string;
  tokens?: Record<string, unknown>;
  inline_css?: boolean;
  sms_signature?: string;
  chat_signature?: string;
  whatsapp_signature?: string;
  airbnb_signature?: string;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** codename + name + description (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1), or 'system' / 'null' for system-wide themes (no tenant). */
  tour_operator_id?: TenantIdsParam;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(
  params: ListParams = {},
): Promise<PaginatedResponse<NotificationThemeListItem>> {
  return apiGet<PaginatedResponse<NotificationThemeListItem>>(
    `/notification_themes/${toQuery(params)}`,
  );
}

export function retrieve(id: number): Promise<NotificationThemeDetail> {
  return apiGet<NotificationThemeDetail>(`/notification_themes/${id}/`);
}

export function create(body: NotificationThemeWrite): Promise<NotificationThemeDetail> {
  return apiPost<NotificationThemeDetail>('/notification_themes/', body);
}

export function update(
  id: number,
  body: NotificationThemeWrite,
): Promise<NotificationThemeDetail> {
  return apiPatch<NotificationThemeDetail>(`/notification_themes/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/notification_themes/${id}/`);
}
